import { useMemo } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';

import { ButtonBase, Tooltip, Typography, Box, styled } from '@mui/material';
import { SxProps } from '@mui/system';

import { DEFAULT_PATH } from 'config';

interface LogoSectionProps {
  reverse?: boolean;
  isIcon?: boolean;
  sx?: SxProps;
  to?: string;
  open?: boolean;
}

const MainText = styled(Typography)({
  fontSize: '15px',
  color: '#007BB5',
  paddingLeft: '15px',
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical'
});

const SubText = styled(Typography)({
  fontSize: '13px',
  color: '#999999',
  paddingLeft: '15px',
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical'
});

function parseLoginCompany(): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('loginCompany');
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return null;
  }
}

function truncate(input: string | undefined, maxLength = 24): string {
  if (!input) return '';
  return input.length > maxLength ? `${input.substring(0, maxLength - 1)}...` : input;
}

function LogoSection({ reverse, isIcon, sx, to, open }: LogoSectionProps) {
  const router = useRouter();
  const isLoginPage = router.pathname.includes('login');
  const isSignupWizard = router.pathname.includes('signup-wizard');

  const loginCompany = useMemo(() => {
    if (isSignupWizard) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('loginCompany');
      }
      return null;
    }
    return parseLoginCompany();
  }, [isSignupWizard]);

  if (isLoginPage) return null;

  const companyName = loginCompany?.company_name || loginCompany?.['company']?.company_name || '';
  const branchName = loginCompany?.branch_name || '';

  return (
    <>
      <NextLink href={to ?? DEFAULT_PATH} passHref>
        <ButtonBase disableRipple sx={sx}>
          <img width="48" src="/assets/images/logo/cw-logo.png" alt="Logo" />
        </ButtonBase>
      </NextLink>
      {open !== false && (
        <Box>
          <Tooltip
            arrow
            title={<Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{companyName}</Typography>}
          >
            <MainText>{truncate(companyName)}</MainText>
          </Tooltip>
          {branchName && (
            <Tooltip
              arrow
              title={<Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{branchName}</Typography>}
            >
              <SubText>{branchName}</SubText>
            </Tooltip>
          )}
        </Box>
      )}
    </>
  );
}

export default LogoSection;
